// Import the functions you need from the SDKs you need
import { FirebaseOptions, initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { getDatabase } from 'firebase/database';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { getFunctions, type Functions } from 'firebase/functions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';

import { firebaseConfig as defaultFirebaseConfig } from './firebase-config';
// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

/**
 * Set by `npm run dev:local`, which points every SDK at local emulators holding
 * only seeded fixtures. See scripts/local/generate-config.js.
 */
const isLocalEnv = import.meta.env.VITE_LOCAL_ENV === 'true';

const emulatorHost = import.meta.env.VITE_EMULATOR_HOST || 'localhost';
const emulatorPort = (name: string): number =>
  Number(import.meta.env[`VITE_EMULATOR_${name}_PORT`]);

const projectId = isLocalEnv
  ? import.meta.env.VITE_FIREBASE_PROJECT_ID
  : 'ai-marketing-agents-406e0';

const databaseUrl = (instance: string): string =>
  isLocalEnv
    ? `http://${emulatorHost}:${emulatorPort('DATABASE')}/?ns=${instance}`
    : `https://${instance}.europe-west1.firebasedatabase.app`;

const firebaseConfig: FirebaseOptions = {
  ...defaultFirebaseConfig,
  databaseURL: databaseUrl(
    import.meta.env.VITE_RTDB_PRESENCE_INSTANCE ||
      'ai-marketing-agents-406e0-presence',
  ),
  projectId,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const rtdb = getDatabase(app);

const limitsRTDB = getDatabase(
  app,
  databaseUrl(
    import.meta.env.VITE_RTDB_LIMITS_INSTANCE ||
      'ai-marketing-agents-406e0-account-limits',
  ),
);

// Configure Functions based on environment
const isDevelopment = import.meta.env.DEV;
const devHostingUrl =
  import.meta.env.VITE_HOSTING_URL || 'http://localhost:5002';
const functions = isDevelopment
  ? getFunctions(app, devHostingUrl)
  : getFunctions(app, 'https://internal-api.superfunnel.ai');

// Every callable attaches a legacy FCM token header, and fetching that token
// re-subscribes this installation with Firebase's default VAPID key, which
// invalidates the FID push registration agent notifications depend on. The
// Functions SDK resolves its messaging dependency lazily, so the property has to
// stay permanently empty rather than being cleared once.
// https://github.com/firebase/firebase-js-sdk/issues/10135
const disableLegacyFunctionsMessagingToken = () => {
  const functionsContextProvider = (
    functions as Functions & {
      contextProvider?: { messaging?: unknown };
    }
  ).contextProvider;
  if (!functionsContextProvider) {
    // Sentry is initialised after this module is evaluated, so a silent
    // `return` here would hide an SDK upgrade that reintroduces the bug for
    // every user. Fail the dev build instead.
    const error = new Error(
      'firebase/functions no longer exposes contextProvider — the FCM token workaround is inert and agent push notifications will break',
    );
    if (isDevelopment) {
      throw error;
    }
    console.error(error);
    return;
  }

  Object.defineProperty(functionsContextProvider, 'messaging', {
    configurable: true,
    get: () => null,
    set: () => undefined,
  });
};

disableLegacyFunctionsMessagingToken();

const auth = getAuth(app);
const storage = getStorage(app); // Default Firebase Storage
const agentFilesStorage = getStorage(app, 'gs://agent-files-f8gmv'); // Agent files bucket

if (isLocalEnv) {
  // Realtime Database instances are already emulator-scoped through their URLs.
  connectAuthEmulator(auth, `http://${emulatorHost}:${emulatorPort('AUTH')}`, {
    disableWarnings: true,
  });
  connectFirestoreEmulator(db, emulatorHost, emulatorPort('FIRESTORE'));
  connectStorageEmulator(storage, emulatorHost, emulatorPort('STORAGE'));
  connectStorageEmulator(
    agentFilesStorage,
    emulatorHost,
    emulatorPort('STORAGE'),
  );
}

export {
  agentFilesStorage,
  app,
  auth,
  db,
  functions,
  limitsRTDB,
  rtdb,
  storage,
};
